export const INBOUND_CALLS_SCOPES = ["core", "all", "care_navigation", "enrollment"] as const;
export type InboundCallsScope = (typeof INBOUND_CALLS_SCOPES)[number];

export type InboundCallsSnapshotParams = {
  end: string;
  scope: InboundCallsScope;
  start: string;
};

export type InboundCallsCoreMetrics = {
  after_hours_calls: number;
  answered_within_30s_pct: number | null;
  business_hours_calls: number;
  business_hours_missed_calls: number;
  destination_numbers: number;
  inbound_calls: number;
  missed_calls: number;
  p50_seconds_to_answer: number | null;
  p50_talk_seconds: number | null;
  p90_seconds_to_answer: number | null;
  picked_up_calls: number;
  pickup_rate_pct: number | null;
  service_level_30s_pct: number | null;
  transferred_calls: number;
  voicemail_calls: number;
};

export type InboundCallsDailyRow = {
  day: string;
  inbound_calls: number;
  missed_calls: number;
  picked_up_calls: number;
  pickup_rate_pct: number | null;
};

export type InboundCallsHourlyRow = {
  hour_et: number;
  inbound_calls: number;
  missed_calls: number;
  picked_up_calls: number;
  pickup_rate_pct: number | null;
};

export type InboundCallsWeeklyHourRow = {
  hour_et: number;
  inbound_calls: number;
  picked_up_calls: number;
  pickup_rate_pct: number | null;
  week_start: string;
};

export type InboundCallsDestinationRow = {
  after_hours_calls: number;
  business_hours_calls: number;
  destination_name: string;
  destination_number: string | null;
  destination_type: string;
  inbound_calls: number;
  missed_calls: number;
  picked_up_calls: number;
  pickup_rate_pct: number | null;
};

export type InboundCallsAnswererRow = {
  answerer_name: string;
  answerer_type: string;
  avg_seconds_to_answer: number | null;
  p50_talk_seconds: number | null;
  picked_up_calls: number;
  transferred_calls: number;
};

export type InboundCallsRoutingFlagRow = {
  destination_name: string;
  destination_number: string | null;
  destination_type: string;
  inbound_calls: number;
  picked_up_calls: number;
  pickup_rate_pct: number | null;
};

export type InboundCallsSnapshotResponse = {
  answerers: InboundCallsAnswererRow[];
  core: InboundCallsCoreMetrics;
  daily: InboundCallsDailyRow[];
  destinations: InboundCallsDestinationRow[];
  end: string;
  generatedAt: string;
  hourly: InboundCallsHourlyRow[];
  routingFlags: InboundCallsRoutingFlagRow[];
  scope: InboundCallsScope;
  start: string;
  weeklyHours: InboundCallsWeeklyHourRow[];
};

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isNullableNumber(value: unknown): boolean {
  return value === null || isFiniteNumber(value);
}

function isCoreMetrics(value: unknown): value is InboundCallsCoreMetrics {
  if (!value || typeof value !== "object") return false;
  const core = value as InboundCallsCoreMetrics;
  return (
    isFiniteNumber(core.inbound_calls) &&
    isFiniteNumber(core.picked_up_calls) &&
    isFiniteNumber(core.missed_calls) &&
    isNullableNumber(core.pickup_rate_pct) &&
    isNullableNumber(core.service_level_30s_pct) &&
    isNullableNumber(core.p90_seconds_to_answer) &&
    isFiniteNumber(core.business_hours_missed_calls) &&
    isFiniteNumber(core.after_hours_calls)
  );
}

export function isInboundCallsSnapshotResponse(
  value: unknown,
): value is InboundCallsSnapshotResponse {
  if (!value || typeof value !== "object") return false;
  const body = value as Partial<InboundCallsSnapshotResponse>;
  return (
    typeof body.start === "string" &&
    typeof body.end === "string" &&
    typeof body.scope === "string" &&
    typeof body.generatedAt === "string" &&
    isCoreMetrics(body.core) &&
    Array.isArray(body.daily) &&
    Array.isArray(body.hourly) &&
    Array.isArray(body.weeklyHours) &&
    Array.isArray(body.destinations) &&
    Array.isArray(body.answerers) &&
    Array.isArray(body.routingFlags)
  );
}
